import type { SignalActionFields, SignalColor, SignalItem, SignalSlot, SignalTextContext, TaskSignal } from '@indipia/shared'
import { parseDeepLink, type ParsedDeepLink } from './deepLink'

// 신호 화면(간트·캘린더·상세창) 공통 보조 — 신호 줄 고르기, 화면 필터, 처리 버튼 주소 읽기.
// 팝오버 · 오늘 할일 패널의 문구 · 버튼 · 주소는 서버(SIG-01 · TODO-01)가 완성해 내려준다(여기서는 만들지 않는다).

/** 점·팝오버 줄 순서: 반장 → 발주 → 작업 (940 시트4 ④ 'C-1 간트 신호등 — 점 3개 = 반장·발주·작업') */
export const SIGNAL_SLOTS: SignalSlot[] = ['FOREMAN', 'ORDER', 'WORK']

export function slotItem(ts: TaskSignal | undefined, slot: SignalSlot): SignalItem | undefined {
  return ts?.signals.find(s => s.signalTypeCode === slot)
}

/** 빨강·노랑 신호가 한 줄이라도 있는 공종 — [빨강·노랑만] 필터 기준 */
export function hasRedYellow(ts: TaskSignal): boolean {
  return ts.signals.some(s => s.colorCode === 'RED' || s.colorCode === 'YELLOW')
}

/**
 * 신호등을 켤 수 없는 스케줄 — 현장 종료(거래완료)로 완료된 스케줄(스케줄 목록 API siteDoneYn='Y').
 * 서버 신호 조회 대상이 아니라 켜면 공종마다 '신호 없음(!)'만 보인다. 공사완료(DONE · 'N')는 막지 않는다(사용자 결정).
 */
export function isSignalUnavailableSchedule(siteDoneYn: string | null | undefined): boolean {
  return siteDoneYn === 'Y'
}

export function isSignalColor(v: string | null | undefined): v is SignalColor {
  return v === 'WHITE' || v === 'YELLOW' || v === 'RED' || v === 'GREEN' || v === 'BLUE' || v === 'NONE'
}

/** 신호 점 읽기 이름(스크린리더) — 색은 점만으로 의미를 전하지 않도록 이름을 함께 준다 */
export const SIGNAL_COLOR_LABEL: Record<SignalColor, string> = {
  WHITE: '흰색', YELLOW: '노랑', RED: '빨강', GREEN: '녹색', BLUE: '파랑', NONE: '해당 없음',
}

/** 신호등 범례 줄의 색 이름 — 반장·발주·작업 점이 쓰는 6색 의미(940 시트4 · 941 S15-2 img066) */
export const SIGNAL_LEGEND_ITEMS: { color: SignalColor; label: string }[] = [
  { color: 'GREEN', label: '정상' },
  { color: 'YELLOW', label: '대기' },
  { color: 'RED', label: '조치 필요' },
  { color: 'BLUE', label: '작업 완료' },
  { color: 'WHITE', label: '아직 아님' },
  { color: 'NONE', label: '해당 없음' },
]

/** 서버가 내려준 처리 버튼(SIG-01 · TODO-01) — actionName 이 null 이면 버튼을 그리지 않는다 */
export type SignalAction = Pick<SignalActionFields, 'actionName' | 'actionTypeCode' | 'webPath' | 'actionUrl'>

/** 인디플래너 화면 주소 — 처리 버튼 주소가 이 경로의 간트(view=gantt)면 화면을 떠나지 않고 그 자리에서 연다 */
const SCHEDULER_PATH = '/agents/scheduler'

/**
 * 처리 버튼 주소가 간트 주소면 그 안의 siteId · taskId · card · pop 을 돌려준다(그 자리에서 여는 용도). 아니면 null.
 * 주소는 서버 할일 정의가 만든다 — 화면은 할일 종류로 행선지를 고르지 않는다.
 */
export function ganttLinkOf(path: string | null | undefined): ParsedDeepLink | null {
  if (!path) return null
  let url: URL
  try { url = new URL(path, 'http://local') } catch { return null }
  if (url.pathname.replace(/\/+$/, '') !== SCHEDULER_PATH || url.searchParams.get('view') !== 'gantt') return null
  return parseDeepLink(url.searchParams)
}

/** 버튼을 그릴지 — 문구가 있고, 화면 이동이면 주소가, 바로 실행이면 호출 주소가 있을 때 */
export function hasSignalAction(a: SignalAction): boolean {
  if (!a.actionName) return false
  if (a.actionTypeCode === 'API') return !!a.actionUrl
  return !!a.webPath
}

/** 인디공사현황 작업보고 확인 화면 주소 — 신호 팝업 · 할일의 [검수하기] · [조치하기]가 여는 곳 */
const WORK_LOG_FIELD_REPORTS_PATH = '/agents/work-log/field-reports'

/**
 * 인디공사현황 작업현황(작업보고) 작성 화면 주소 — 스케줄 · 공정을 넘기면 작성 화면이 그 현장(과 공정의 공종)을 미리 고른다
 * (`?scheduleId=&taskId=`, FieldReportOwnerWritePage). [작업요청/현황 ▾ > 작업현황 작성] · 상세창 진행 요약 [작업현황 등록 ›]이 쓴다.
 */
export function workStatusWritePath(scheduleId: number | null | undefined, taskId?: number | null): string {
  const q = new URLSearchParams()
  if (scheduleId != null) q.set('scheduleId', String(scheduleId))
  if (scheduleId != null && taskId != null) q.set('taskId', String(taskId))
  const qs = q.toString()
  return qs ? `${WORK_LOG_FIELD_REPORTS_PATH}/new?${qs}` : `${WORK_LOG_FIELD_REPORTS_PATH}/new`
}

/**
 * 작업보고 확인 주소에 현장(siteId)이 없으면 붙인다 — 신호 팝업 · 할일에서 열 때 그 현장을 기본으로 선택하게 한다.
 * 서버 할일 주소(TM_SITE_TODO)에는 현장이 없어(작업요청 작성 주소만 siteId 가 있다) 화면이 아는 현장을 더한다.
 * 다른 주소 · 이미 siteId 가 있는 주소 · 현장을 모르는 경우는 그대로 돌려준다.
 */
export function withSiteId(path: string, siteId: number | null | undefined): string {
  if (siteId == null || !Number.isInteger(siteId) || siteId <= 0) return path
  let url: URL
  try { url = new URL(path, 'http://local') } catch { return path }
  if (url.pathname.replace(/\/+$/, '') !== WORK_LOG_FIELD_REPORTS_PATH || url.searchParams.has('siteId')) return path
  url.searchParams.set('siteId', String(siteId))
  return `${url.pathname}${url.search}${url.hash}`
}

export interface SignalTaskContext {
  startDate?: string | null
  endDate?: string | null
  partnerName?: string | null
  rank?: number | null
  vendorName?: string | null
}

/** 문구 조립 값 — 신호 응답의 reasonDate·reasonDtm·nextJudgeDtm 에 화면이 가진 공종 값을 더한다 */
export function signalCtx(item: SignalItem, t: SignalTaskContext): SignalTextContext {
  return {
    reasonDate: item.reasonDate,
    reasonDtm: item.reasonDtm,
    nextJudgeDtm: item.nextJudgeDtm,
    startDate: t.startDate ?? null,
    endDate: t.endDate ?? null,
    partnerName: t.partnerName ?? null,
    rank: t.rank ?? null,
    vendorName: t.vendorName ?? null,
  }
}

/** Date → 'YYYY-MM-DD' (로컬 달력일) */
// 'YYYY-MM-DD' 변환은 공용(@indipia/shared toIsoDay) — 기존 호출부 이름 유지
export { toIsoDay as isoDay } from '@indipia/shared'
